import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { formatQty, UNITS } from '@tengesa/shared';
import { rangeFor, RANGE_OPTIONS, type RangeKey } from '../lib/dates';
import type { ProductDto } from '../lib/types';
import { Select, TextInput } from './index';
import { ProductPicker, QtyInput } from './inputs';

/** Period state for report-style pages: a preset or two chosen dates. */
export function usePeriod(initial: RangeKey = '30d') {
  const [key, setKey] = useState<RangeKey>(initial);
  const [custom, setCustom] = useState(rangeFor('30d'));
  return { key, setKey, custom, setCustom, ...rangeFor(key, custom) };
}

export function PeriodPicker({ period }: { period: ReturnType<typeof usePeriod> }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select aria-label="Period" className="max-w-56" value={period.key} onChange={(e) => period.setKey(e.target.value as RangeKey)}>
        {RANGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
      {period.key === 'custom' && (
        <>
          <TextInput aria-label="From" type="date" className="max-w-44" value={period.custom.from} onChange={(e) => period.setCustom({ ...period.custom, from: e.target.value })} />
          <span className="text-muted">to</span>
          <TextInput aria-label="To" type="date" className="max-w-44" value={period.custom.to} onChange={(e) => period.setCustom({ ...period.custom, to: e.target.value })} />
        </>
      )}
    </div>
  );
}

export interface QtyLine {
  key: string;
  product: ProductDto;
  qtyMilli: number | null;
}

/** A list of products with a quantity each (wastage, carcass cuts). */
export function ProductQtyLines({
  lines,
  onChange,
  branchId,
  pickerLabel,
  emptyText,
  extra,
}: {
  lines: QtyLine[];
  onChange: (lines: QtyLine[]) => void;
  branchId?: string | null;
  pickerLabel: string;
  emptyText: string;
  extra?: (l: QtyLine) => React.ReactNode;
}) {
  return (
    <div>
      <ProductPicker
        label={pickerLabel}
        branchId={branchId}
        sellable={false}
        stockedOnly
        onPick={(p) => {
          if (lines.some((l) => l.product.id === p.id)) return;
          onChange([...lines, { key: `${p.id}-${Date.now()}`, product: p, qtyMilli: null }]);
        }}
      />
      {lines.length === 0 ? (
        <p className="mt-3 rounded-[8px] border border-dashed border-line-strong px-4 py-5 text-center text-sm text-muted">{emptyText}</p>
      ) : (
        <ul className="mt-3 divide-y divide-line rounded-[8px] border border-line">
          {lines.map((l) => (
            <li key={l.key} className="grid grid-cols-[1fr_140px_auto] items-center gap-3 px-3 py-2">
              <span className="min-w-0">
                <span className="block truncate font-medium">{l.product.name}</span>
                <span className="block text-xs text-muted">
                  {l.product.sku}
                  {l.product.stock ? ` — ${formatQty(l.product.stock.qtyMilli)} ${UNITS[l.product.unit as keyof typeof UNITS]?.short ?? ''} in stock` : ''}
                  {extra?.(l)}
                </span>
              </span>
              <QtyInput
                aria-label={`Quantity of ${l.product.name}`}
                unit={l.product.unit}
                value={l.qtyMilli}
                onChange={(q) => onChange(lines.map((x) => (x.key === l.key ? { ...x, qtyMilli: q } : x)))}
              />
              <button className="rounded-md p-1.5 text-muted hover:bg-ink/5 hover:text-danger" aria-label={`Remove ${l.product.name}`} onClick={() => onChange(lines.filter((x) => x.key !== l.key))}>
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
